// src/theme/index.js
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  `onSuccess` for the green done state, and `mark` / `onMark` for
//               the recognition medallion, which is dark in both modes because
//               a coin does not invert. Gold now belongs to marks only.
//   2026-08-27  Dark mode. Every palette value moved from a flat color to a
//               semanticTokens pair so `bg="bg"` and friends resolve per color
//               mode instead of being locked to light. Token NAMES are
//               unchanged on purpose, so the ~200 existing call sites keep
//               working untouched. Two new tokens: `card` (replaces literal
//               bg="white") and `onInk` (replaces literal color="white" on ink
//               fills). initialColorMode is 'system' so we inherit the phone.
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
//   2026-07-23  Ink ramp replaces blue brand; oxblood accent, pill buttons
// NEXT: persist the user's choice to users.color_mode in Supabase so it follows
//       a salesperson from the floor phone to the desk machine.
//
// Chakra UI v2 theme for the J|13 Dealer Academy.
//
// Design intent: this is a daily-use operational tool, not a storytelling page.
// A salesperson opens it between customers, often outdoors, often in a bright
// showroom. Light is therefore the honest default, and dark is a real, equal
// alternative rather than an inverted afterthought.
//
// The language comes from Jazz's own printed materials: warm off-white paper,
// heavy black display type, oxblood used structurally (eyebrows, rules,
// bullets) and never decoratively, and occasional full-contrast blocks for the
// moments that carry weight.
//
// HOW `ink` WORKS, because it is the one surprising thing in here.
// `ink` is the emphasis colour, not "black". Light mode: near-black.
// Dark mode: near-white. So `bg="ink" color="onInk"` is a black block with
// white text on light, and a paper block with black text on dark. The block
// stays the loudest thing on the page either way, which is what it was for.
// Always pair `bg="ink"` with `color="onInk"`, never with a literal.

import { extendTheme } from '@chakra-ui/react'

// Raw values. Nothing here is consumed directly by components; the semantic
// layer below is the public surface.
const palette = {
  // paper
  offWhite: '#FAFAF7',
  paper: '#FFFFFF',
  stone: '#F2F2EE',
  stone2: '#E8E8E3',

  // night. Warm-shifted rather than pure grey so it sits under the oxblood
  // without going blue, and so the two modes read as one family.
  night: '#0B0B0C',
  night2: '#131314',
  night3: '#1B1B1D',
  night4: '#242427',

  // ink ramp
  nearBlack: '#0A0A0A',
  black2: '#1D1D1F',
  greyMid: '#4A4A4F',
  greyDim: '#68686D',
  paperInk: '#EDEDEF',
  paperInk2: '#DCDCE0',
  greyOnDark: '#9A9AA0',
  greyDimOnDark: '#8A8A90',

  // CONTRAST IS MEASURED HERE, NOT JUDGED BY EYE
  //
  // Every colour below that carries text was solved against EVERY ground it
  // can land on, to 4.5:1, which is the WCAG AA floor for normal text. Solving
  // against one ground is how a colour passes on the page and fails inside a
  // card, which is exactly what was happening.
  //
  // Measured before this pass: inkDim was 3.46 on light and 3.88 on dark, so
  // every caption, eyebrow and timestamp in the product sat below the floor.
  // gold on light was 2.29, which is under even the large-text minimum of 3.
  //
  // oxblood, from the J13 cover art. Lightened on dark so it clears contrast
  // against night without turning pink.
  oxblood: '#9B2D2D',
  oxbloodDeep: '#7A2323',
  ember: '#E2705E',
  emberDeep: '#C85B4B',

  gold: '#88610A',
  goldOnDark: '#D9B569',
  success: '#1B8845',
  successOnDark: '#4ADE80',
  warn: '#B45309',
  warnOnDark: '#E9A23B',
  danger: '#B91C1C',
  dangerOnDark: '#F0736B',
}

// The ink ramp doubles as `brand`, so every legacy brand.500 reference renders
// as the emphasis colour rather than the retired blue.
const colors = {
  brand: {
    50: '#F2F2F2',
    100: '#D9D9D9',
    200: '#B3B3B3',
    300: '#808080',
    400: '#333333',
    500: palette.nearBlack,
    600: '#000000',
    700: '#000000',
    800: '#000000',
    900: '#000000',
  },
}

const semanticTokens = {
  colors: {
    // surfaces
    bg: { _light: palette.offWhite, _dark: palette.night },
    card: { _light: palette.paper, _dark: palette.night2 },
    surface: { _light: palette.stone, _dark: palette.night3 },
    surface2: { _light: palette.stone2, _dark: palette.night4 },

    // type + emphasis. See the note at the top of this file: `ink` flips.
    ink: { _light: palette.nearBlack, _dark: palette.paperInk },
    ink2: { _light: palette.black2, _dark: palette.paperInk2 },
    inkMuted: { _light: palette.greyMid, _dark: palette.greyOnDark },
    inkDim: { _light: palette.greyDim, _dark: palette.greyDimOnDark },
    onInk: { _light: palette.paper, _dark: palette.nearBlack },

    // Foregrounds and fills used INSIDE an `bg="ink"` block. These have to
    // invert with it: on light the block is black so they are white-ish, on
    // dark the block is paper so they are black-ish. Using whiteAlpha.* here
    // renders white-on-white the moment the block flips.
    onInkStrong: {
      _light: 'rgba(255, 255, 255, 0.92)',
      _dark: 'rgba(10, 10, 10, 0.88)',
    },
    onInkMuted: {
      _light: 'rgba(255, 255, 255, 0.82)',
      _dark: 'rgba(10, 10, 10, 0.74)',
    },
    onInkSubtle: {
      _light: 'rgba(255, 255, 255, 0.70)',
      _dark: 'rgba(10, 10, 10, 0.60)',
    },
    onInkFill: {
      _light: 'rgba(255, 255, 255, 0.20)',
      _dark: 'rgba(10, 10, 10, 0.10)',
    },
    onInkFill2: {
      _light: 'rgba(255, 255, 255, 0.30)',
      _dark: 'rgba(10, 10, 10, 0.16)',
    },

    // The floating bottom nav sits over scrolling content, so it needs a
    // ground that is see-through without letting a lesson thumbnail render the
    // icons unreadable. Card colour at 72% plus a backdrop blur: the content
    // stays visible underneath and moves under the bar, which is the point of
    // asking for transparency, while the labels keep their contrast.
    navGlass: {
      _light: 'rgba(255, 255, 255, 0.72)',
      _dark: 'rgba(19, 19, 20, 0.72)',
    },

    // rules
    line: {
      _light: 'rgba(10, 10, 10, 0.09)',
      _dark: 'rgba(255, 255, 255, 0.11)',
    },
    lineStrong: {
      _light: 'rgba(10, 10, 10, 0.17)',
      _dark: 'rgba(255, 255, 255, 0.22)',
    },

    // brand.500 has to flip too. It is the legacy alias for the emphasis colour
    // from the blue retirement, and a raw entry in `colors` cannot vary by mode,
    // so anything still saying bg="brand.500" would render a black block on a
    // black page. Overriding it here makes the alias behave like `ink`.
    'brand.500': { _light: palette.nearBlack, _dark: palette.paperInk },

    // accent. Structural only: eyebrows, bullets, left rules, active marks.
    'accent.500': { _light: palette.oxblood, _dark: palette.ember },
    'accent.600': { _light: palette.oxbloodDeep, _dark: palette.emberDeep },
    'accent.soft': {
      _light: 'rgba(155, 45, 45, 0.08)',
      _dark: 'rgba(226, 112, 94, 0.15)',
    },

    inkSoft: {
      _light: 'rgba(10, 10, 10, 0.05)',
      _dark: 'rgba(255, 255, 255, 0.06)',
    },

    gold: { _light: palette.gold, _dark: palette.goldOnDark },
    // A MARK'S DISC AND WHAT IS STRUCK ON IT. Dark in both modes on purpose,
    // unlike `ink`: a coin does not invert, and light gold on the paper disc
    // `ink` becomes on dark measured under 2:1. Warm near black on light, a
    // lifted warm grey on dark so the disc still separates from the page. The
    // glyph is the on-dark gold in both, which clears 7:1 on either disc.
    // Added 2026-09-27 with marks; see src/components/ui/Medallion.jsx.
    mark: { _light: '#171411', _dark: '#2B251B' },
    onMark: { _light: palette.goldOnDark, _dark: palette.goldOnDark },
    goldSoft: {
      _light: 'rgba(201, 162, 75, 0.10)',
      _dark: 'rgba(217, 181, 105, 0.15)',
    },
    success: { _light: palette.success, _dark: palette.successOnDark },
    // What goes ON a success fill: a tick, a number. White on the light-mode
    // green, near black on the dark-mode green, because #4ADE80 is bright
    // enough that a white tick on it is close to invisible. Added 2026-09-27
    // with the green done-state Tyler asked for, which is where it showed.
    onSuccess: { _light: '#FFFFFF', _dark: '#0B2A16' },
    successSoft: {
      _light: 'rgba(27, 136, 69, 0.10)',
      _dark: 'rgba(74, 222, 128, 0.14)',
    },
    warn: { _light: palette.warn, _dark: palette.warnOnDark },
    warnSoft: {
      _light: 'rgba(180, 83, 9, 0.10)',
      _dark: 'rgba(233, 162, 59, 0.14)',
    },
    danger: { _light: palette.danger, _dark: palette.dangerOnDark },
    dangerSoft: {
      _light: 'rgba(185, 28, 28, 0.10)',
      _dark: 'rgba(240, 115, 107, 0.14)',
    },
  },

  shadows: {
    card: {
      _light: '0 1px 2px rgba(10,10,10,0.04), 0 8px 24px rgba(10,10,10,0.04)',
      _dark: '0 1px 2px rgba(0,0,0,0.5), 0 8px 24px rgba(0,0,0,0.35)',
    },
    lift: {
      _light: '0 2px 4px rgba(10,10,10,0.05), 0 14px 32px rgba(10,10,10,0.07)',
      _dark: '0 2px 4px rgba(0,0,0,0.55), 0 14px 32px rgba(0,0,0,0.45)',
    },
  },
}

const fonts = {
  heading: `'General Sans', system-ui, -apple-system, sans-serif`,
  body: `'General Sans', system-ui, -apple-system, sans-serif`,
  display: `'General Sans', system-ui, -apple-system, sans-serif`,
  mono: `'JetBrains Mono', ui-monospace, monospace`,
}

const radii = {
  card: '16px',
  cardLg: '20px',
  pill: '980px',
  input: '12px',
}

// Page geometry. Every authenticated page is a SHEET: one column, left aligned
// against the sidebar, never centred in the viewport. Centring made each page
// float in a different place as the content changed width, so the eye had to
// re-find the start of the line on every navigation. Left aligned means the
// first character sits in the same spot on every screen.
//
// Use `maxW="sheet"` on the page container and do NOT add mx="auto".
// Use `maxW="measure"` on long-form prose inside a sheet, because a sheet is
// wider than comfortable reading and body copy should not run the full width.
const sizes = {
  sheet: '920px',
  measure: '68ch',
}

const fontSizes = {
  'display-xl': 'clamp(38px, 4.5vw, 60px)',
  'display-lg': 'clamp(29px, 3vw, 38px)',
  'display-md': 'clamp(21px, 2.1vw, 27px)',
  'display-sm': '18px',
  lead: 'clamp(15px, 1.3vw, 17px)',
  'body-lg': '16px',
  body: '15px',
  'body-sm': '14px',
  'mono-sm': '12px',
  'mono-xs': '11px',
}

// The eyebrow is the structural device carried over from Jazz's printed
// materials. Mono, uppercase, letterspaced, oxblood. It labels a section; it
// never decorates one.
const textStyles = {
  eyebrow: {
    fontFamily: 'mono',
    fontSize: 'mono-sm',
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'accent.500',
  },
  eyebrowMuted: {
    fontFamily: 'mono',
    fontSize: 'mono-xs',
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'inkDim',
  },
  // Every authenticated page opens the same way: a small quiet label, then the
  // title. These two exist because that pair had drifted into five different
  // treatments across nine pages. Today was display-lg at weight 500 and the
  // rest were display-md at 600, a whole step smaller, which is why Today read
  // better than everything else and nobody could say why.
  //
  // Use `textStyle="pageTitle"` and `textStyle="pageEyebrow"` on every page
  // header. Do not set fontSize on a page title by hand.
  pageTitle: {
    fontFamily: 'display',
    fontSize: 'display-lg',
    fontWeight: 500,
    letterSpacing: '-0.022em',
    lineHeight: 1.1,
    color: 'ink',
  },
  pageEyebrow: {
    fontSize: 'body-sm',
    fontWeight: 500,
    color: 'inkMuted',
    letterSpacing: '0',
  },

  metric: {
    fontFamily: 'mono',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em',
  },
}

const styles = {
  global: {
    'html, body': {
      bg: 'bg',
      color: 'ink',
      fontFamily: 'body',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      textRendering: 'optimizeLegibility',
    },
    // Home-screen installs paint behind the app; keep that ground on-theme.
    '#root': { bg: 'bg', minH: '100vh' },
    '::selection': { background: 'ink', color: 'onInk' },
    'code, kbd, pre, samp': {
      fontFamily: 'mono',
      fontVariantNumeric: 'tabular-nums',
    },
    // Visible keyboard focus everywhere. Managers work on desk machines and tab.
    ':focus-visible': {
      outline: '2px solid',
      outlineColor: 'accent.500',
      outlineOffset: '2px',
    },
  },
}

const components = {
  // Toasts and inline alerts, in the app's own language.
  //
  // Chakra's toast is an Alert with variant "solid", and unthemed that is a
  // flat Chakra green banner that appears nowhere else in this product. It was
  // the one surface still shipping somebody else's design system.
  //
  // The palette already had success, warn, danger and their Soft pairs and
  // nothing used them, so this spends what was already defined rather than
  // inventing a colour. The status colour is deliberately kept to a 3px left
  // rule and the icon: the toast reads as a card off the same shelf as every
  // other card, and the colour is a signal rather than a wash.
  //
  // Only "solid" is redefined for toasts. "subtle" is redefined too because the
  // inline <Alert> in Coach, Settings and Team was also rendering Chakra's tints.
  Alert: {
    baseStyle: {
      container: { borderRadius: 'card' },
      title: { fontWeight: 600, letterSpacing: '-0.005em' },
    },
    variants: {
      solid: (props) => {
        const tone =
          props.status === 'error' ? 'danger'
            : props.status === 'warning' ? 'warn'
              : props.status === 'success' ? 'success'
                : 'ink'
        return {
          container: {
            bg: 'card',
            color: 'ink',
            border: '1px solid',
            borderColor: 'line',
            borderLeft: '3px solid',
            borderLeftColor: tone,
            boxShadow: 'card',
            borderRadius: 'card',
          },
          icon: { color: tone },
          description: { color: 'inkMuted' },
        }
      },
      subtle: (props) => {
        const tone =
          props.status === 'error' ? 'danger'
            : props.status === 'warning' ? 'warn'
              : props.status === 'success' ? 'success'
                : 'ink'
        const wash =
          props.status === 'error' ? 'dangerSoft'
            : props.status === 'warning' ? 'warnSoft'
              : props.status === 'success' ? 'successSoft'
                : 'surface'
        return {
          container: {
            bg: wash,
            color: 'ink',
            borderLeft: '3px solid',
            borderLeftColor: tone,
            borderRadius: 'card',
          },
          icon: { color: tone },
          description: { color: 'inkMuted' },
        }
      },
    },
    defaultProps: { variant: 'subtle' },
  },

  // Chakra's default MenuItem renders about 29px tall. This app is used one
  // handed on a sales floor, and every touch guideline puts the minimum around
  // 44px. Setting it here rather than per instance because it should be true of
  // any menu anyone adds later, and because passing minH on the component was
  // partially overridden by Chakra's own item padding: it got to 38px and
  // stopped, which is the kind of almost-fixed that looks fixed in a diff.
  Menu: {
    baseStyle: {
      item: {
        minHeight: '44px',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem',
      },
    },
  },
  Button: {
    baseStyle: {
      fontWeight: 500,
      borderRadius: 'pill',
      letterSpacing: '-0.005em',
    },
    variants: {
      solid: {
        bg: 'ink',
        color: 'onInk',
        _hover: { bg: 'ink2', _disabled: { bg: 'ink' } },
        _active: { bg: 'ink2' },
      },
      outline: {
        borderColor: 'lineStrong',
        color: 'ink',
        _hover: { bg: 'surface' },
      },
      ghost: {
        color: 'ink',
        _hover: { bg: 'surface' },
      },
      // Oxblood keeps white text in both modes; it is dark enough on light and
      // saturated enough on dark to carry it.
      accent: {
        bg: 'accent.500',
        color: 'white',
        _hover: { bg: 'accent.600' },
        _active: { bg: 'accent.600' },
      },
    },
    defaultProps: { variant: 'solid', size: 'md' },
  },
  Input: {
    variants: {
      outline: {
        field: {
          borderColor: 'lineStrong',
          borderRadius: 'input',
          bg: 'card',
          color: 'ink',
          _hover: { borderColor: 'inkMuted' },
          _focus: {
            borderColor: 'ink',
            boxShadow: '0 0 0 1px var(--chakra-colors-ink)',
          },
          _placeholder: { color: 'inkDim' },
        },
      },
    },
    defaultProps: { variant: 'outline' },
  },
  Textarea: {
    variants: {
      outline: {
        borderColor: 'lineStrong',
        borderRadius: 'input',
        bg: 'card',
        color: 'ink',
        _hover: { borderColor: 'inkMuted' },
        _focus: {
          borderColor: 'ink',
          boxShadow: '0 0 0 1px var(--chakra-colors-ink)',
        },
        _placeholder: { color: 'inkDim' },
      },
    },
    defaultProps: { variant: 'outline' },
  },
  Heading: {
    baseStyle: {
      fontFamily: 'display',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      color: 'ink',
    },
  },
  FormLabel: {
    baseStyle: {
      fontSize: 'xs',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'inkMuted',
      mb: 2,
    },
  },
  Divider: {
    baseStyle: { borderColor: 'line', opacity: 1 },
  },
  Progress: {
    baseStyle: {
      track: { bg: 'surface2' },
      filledTrack: { bg: 'ink' },
    },
  },
}

const theme = extendTheme({
  colors,
  semanticTokens,
  fonts,
  fontSizes,
  radii,
  sizes,
  textStyles,
  styles,
  components,
  config: {
    // Follow the device. A salesperson whose phone runs dark gets a dark
    // Academy without being asked; the Settings toggle overrides and sticks.
    initialColorMode: 'system',
    useSystemColorMode: false,
  },
})

export default theme
